import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db, storage } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, doc, getDoc, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { Icon } from './icons';
import { User, Users, Settings, Briefcase, MapPin, Link as LinkIcon, CheckCircle, XCircle, Loader, Award, BookOpen, CreditCard, Layout, Camera, Edit2, Save, X, Building2 } from './icons';
import ProfileSettings from './ProfileSettings';
import PrivacySettings from './PrivacySettings';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import { navigateTo } from '../utils/navigationHelper';
import { connectGoogleCalendar, loadGoogleIdentityServices } from '../utils/googleCalendar';
import { linkedinOAuth } from '../services/oauth/linkedinOAuth';
import connectionMatchingService, { UserWithMatchData } from '../services/connectionMatchingService';
import { useEntityContext } from '../hooks/useEntityContext';
import { EntitySwitcher } from './roles/EntitySwitcher';
import CreateEntityModal from './entityManagement/CreateEntityModal';
import JoinEntityModal from './entityManagement/JoinEntityModal';
import AccessRolesSection from './roles/AccessRolesSection';
import InviteRoleForm from './roles/InviteRoleForm';
import PermissionsMatrix from './roles/PermissionsMatrix';
import ManagerAccessManagement from './ManagerAccessManagement';
import AccountTransferModal from './AccountTransferModal';

const COUNTRY_FLAGS: Record<string, string> = {
  'Nederland': '🇳🇱', 'België': '🇧🇪', 'Duitsland': '🇩🇪', 'Frankrijk': '🇫🇷',
  'Verenigd Koninkrijk': '🇬🇧', 'Spanje': '🇪🇸', 'Italië': '🇮🇹', 'Oostenrijk': '🇦🇹',
  'Zwitserland': '🇨🇭', 'Denemarken': '🇩🇰', 'Zweden': '🇸🇪', 'Noorwegen': '🇳🇴',
  'Finland': '🇫🇮', 'Polen': '🇵🇱', 'Tsjechië': '🇨🇿', 'Hongarije': '🇭🇺',
  'Portugal': '🇵🇹', 'Griekenland': '🇬🇷', 'Ierland': '🇮🇪', 'Luxemburg': '🇱🇺',
  'Andere': '🌍',
};

interface ProfileHubProps {
  userProfile?: any;
  onClose?: () => void;
  onProfileUpdate?: (updatedProfile: any) => void;
  displaySettings?: {
    focusMode: boolean;
    energyMode: boolean;
    blueLightFilter: boolean;
    dayNightMode: 'day' | 'night';
  };
  onDisplaySettingsChange?: (settings: any) => void;
}

interface OnboardingData {
  accountType?: 'entrepreneur' | 'manager' | null;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  company?: string;
  companyLocation?: string;
  city?: string;
  streetAddress?: string;
  postalCode?: string;
  phoneNumber?: string;
  website?: string;
  workScope?: 'lokaal' | 'nationaal' | 'internationaal';
  region?: string;
  country?: string;
  expertise?: string[];
  interests?: string[];
  eventPrefs?: {
    regions?: string[];
    mode?: 'online' | 'hybride' | 'fysiek';
  };
  photoPosition?: { x: number; y: number };
  networkclubKvk?: string; // KVK number for company
  kvkNumber?: string; // Alternative field name
}

interface SocialConnection {
  platform: string;
  connected: boolean;
  username?: string;
  accessToken?: string;
}

// Image compression utility using Canvas API
const compressImage = (file: File, maxWidth: number = 800, quality: number = 0.85): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Determine output format
        const isPNG = file.type === 'image/png';
        const outputFormat = isPNG ? 'image/png' : 'image/jpeg';
        const outputExtension = isPNG ? '.png' : '.jpg';
        
        // Convert to blob
        const blobCallback = (blob: Blob | null) => {
          if (!blob) {
            reject(new Error('Could not compress image'));
            return;
          }
          
          // Create new file from blob
          const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, outputExtension), {
            type: outputFormat,
            lastModified: Date.now(),
          });
          
          console.log(`📸 Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
          resolve(compressedFile);
        };
        
        // Call toBlob with quality parameter for JPEG
        if (outputFormat === 'image/png') {
          canvas.toBlob(blobCallback, outputFormat);
        } else {
          canvas.toBlob(blobCallback, outputFormat, quality);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Could not load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Could not read file'));
    };
  });
};

const ACCESS_NOTICE_KEY = 'accessNotice';

const ProfileHub: React.FC<ProfileHubProps> = ({ userProfile, onClose, onProfileUpdate, displaySettings, onDisplaySettingsChange }) => {
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'settings'>('overview');
  
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({});
  const [editedData, setEditedData] = useState<OnboardingData>({});
  const [socialConnections, setSocialConnections] = useState<SocialConnection[]>([]);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempFieldValue, setTempFieldValue] = useState<string>('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showImagePositioner, setShowImagePositioner] = useState(false);
  const [tempPhotoURL, setTempPhotoURL] = useState<string>('');
  const [tempPhotoFile, setTempPhotoFile] = useState<File | null>(null);
  const [tempPhotoPosition, setTempPhotoPosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [accessNotice, setAccessNotice] = useState<string | null>(() => sessionStorage.getItem(ACCESS_NOTICE_KEY));
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [termsOfServiceOpen, setTermsOfServiceOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const accessNoticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const memberSnapshotInit = useRef(true);
  const permissionsSnapshotInit = useRef(true);
  const [topMatches, setTopMatches] = useState<UserWithMatchData[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [userSubscription, setUserSubscription] = useState<{ tier: string; lifetimeAccessNumber?: number } | null>(null);
  const [showVaInvite, setShowVaInvite] = useState(false);
  const [showBoardInvite, setShowBoardInvite] = useState(false);

  // ── Mobile-only: Clean Mind Filter state (mirrors CommunityDashboard via localStorage) ──
  const [cleanMindEnabled, setCleanMindEnabledLocal] = useState<boolean>(() => {
    try { return JSON.parse(localStorage.getItem('cleanMindEnabled') || 'false'); } catch { return false; }
  });
  const [cleanFilters, setCleanFiltersLocal] = useState<{ videos: boolean; images: boolean; photos: boolean; text: boolean; ai: boolean }>(() => {
    try {
      const saved = localStorage.getItem('cleanFilters');
      if (saved) return { ai: true, ...JSON.parse(saved) };
    } catch {}
    return { videos: true, images: true, photos: true, text: true, ai: true };
  });
  const [removeEmojis, setRemoveEmojisLocal] = useState<boolean>(() => {
    try { return JSON.parse(localStorage.getItem('cleanRemoveEmojis') || 'true'); } catch { return true; }
  });

  // Write to localStorage and notify CommunityDashboard via custom event
  const updateCleanMind = (val: boolean) => {
    setCleanMindEnabledLocal(val);
    try { localStorage.setItem('cleanMindEnabled', JSON.stringify(val)); } catch {}
    window.dispatchEvent(new CustomEvent('settingsChanged'));
  };
  const updateCleanFilters = (fn: (prev: typeof cleanFilters) => typeof cleanFilters) => {
    setCleanFiltersLocal(prev => {
      const next = fn(prev);
      try { localStorage.setItem('cleanFilters', JSON.stringify(next)); } catch {}
      window.dispatchEvent(new CustomEvent('settingsChanged'));
      return next;
    });
  };
  const updateRemoveEmojis = (val: boolean) => {
    setRemoveEmojisLocal(val);
    try { localStorage.setItem('cleanRemoveEmojis', JSON.stringify(val)); } catch {}
    window.dispatchEvent(new CustomEvent('settingsChanged'));
  };
  const [showCreateEntity, setShowCreateEntity] = useState(false);
  const [showJoinEntity, setShowJoinEntity] = useState(false);
  const [showAccountTransfer, setShowAccountTransfer] = useState(false);
  const {
    entities,
    activeEntityId,
    setActiveEntityId,
    loading: entitiesLoading,
    error: entitiesError
  } = useEntityContext(user?.uid);
  const activeEntity = entities.find(entity => entity.id === activeEntityId) || null;
  const roleLabel = activeEntity?.role
    ? {
        owner: 'Eigenaar',
        organizer: 'Organisator',
        board: 'Bestuurslid',
        va: 'V,A',
        member: 'Lid'
      }[activeEntity.role]
    : '';
  const canManagePermissions = activeEntity?.role === 'owner' || activeEntity?.role === 'organizer';

  useEffect(() => {
    if (!accessNotice) {
      sessionStorage.removeItem(ACCESS_NOTICE_KEY);
      return;
    }
    sessionStorage.setItem(ACCESS_NOTICE_KEY, accessNotice);
    const clearNotice = setTimeout(() => {
      sessionStorage.removeItem(ACCESS_NOTICE_KEY);
      setAccessNotice(null);
    }, 8000);

    return () => clearTimeout(clearNotice);
  }, [accessNotice]);

  useEffect(() => {
    if (!user?.uid || !activeEntityId) return;

    memberSnapshotInit.current = true;
    permissionsSnapshotInit.current = true;

    const triggerAccessRefresh = (message: string) => {
      sessionStorage.setItem(ACCESS_NOTICE_KEY, message);
      setAccessNotice(message);
      if (accessNoticeTimeoutRef.current) {
        clearTimeout(accessNoticeTimeoutRef.current);
      }
      accessNoticeTimeoutRef.current = setTimeout(() => {
        window.location.reload();
      }, 800);
    };

    const memberRef = doc(db, 'entityMembers', `${activeEntityId}_${user.uid}`);
    const unsubscribeMember = onSnapshot(memberRef, (snapshot) => {
      if (memberSnapshotInit.current) {
        memberSnapshotInit.current = false;
        return;
      }
      if (!snapshot.exists()) {
        triggerAccessRefresh('Je toegang tot deze entiteit is ingetrokken. De pagina wordt ververst.');
        return;
      }
      const nextRole = snapshot.data()?.role;
      if (nextRole && activeEntity?.role && nextRole !== activeEntity.role) {
        triggerAccessRefresh('Je rol is gewijzigd. De pagina wordt ververst.');
      }
    });

    const permissionsQuery = query(
      collection(db, 'permissions'),
      where('entityId', '==', activeEntityId),
      where('userId', '==', user.uid)
    );
    const unsubscribePermissions = onSnapshot(permissionsQuery, (snapshot) => {
      if (permissionsSnapshotInit.current) {
        permissionsSnapshotInit.current = false;
        return;
      }
      if (snapshot.docChanges().length === 0) return;
      triggerAccessRefresh('Je rechten zijn aangepast. De pagina wordt ververst.');
    });

    return () => {
      unsubscribeMember();
      unsubscribePermissions();
      if (accessNoticeTimeoutRef.current) {
        clearTimeout(accessNoticeTimeoutRef.current);
      }
    };
  }, [user?.uid, activeEntityId, activeEntity?.role]);

  const loadTopMatches = async () => {
    if (!user?.uid || !userProfile) return;
    
    try {
      setMatchesLoading(true);
      const { collection, query, where, getDocs, limit } = await import('firebase/firestore');
      
      // Haal eerste 5 "open voor netwerk" gebruikers op
      const openForConnectionsQuery = query(
        collection(db, 'openForConnections'),
        where('isActive', '==', true),
        limit(5)
      );
      const snapshot = await getDocs(openForConnectionsQuery);
      
      const openUsers = snapshot.docs.map(doc => ({
        userId: doc.data().userId,
        userName: doc.data().userName,
        userEmail: doc.data().userEmail,
        userPhoto: doc.data().userPhoto,
        userCompany: doc.data().userCompany,
        userRegion: doc.data().userRegion,
        userRegions: doc.data().userRegions || [],
        userBio: doc.data().userBio,
        subscriptionTier: doc.data().subscriptionTier || 'free'
      })).filter(u => u.userId !== user.uid); // Filter current user

      if (openUsers.length > 0) {
        // Voeg match data toe
        const enrichedUsers = await connectionMatchingService.enrichUsersWithMatchData(
          user.uid,
          userProfile,
          openUsers
        );

        // Sorteer op match score en neem top 5
        enrichedUsers.sort((a, b) => {
          if (a.matchData.sharedConnections !== b.matchData.sharedConnections) {
            return b.matchData.sharedConnections - a.matchData.sharedConnections;
          }
          return b.matchData.matchScore - a.matchData.matchScore;
        });

        setTopMatches(enrichedUsers.slice(0, 5));
      }
    } catch (error) {
      console.error('Error loading top matches:', error);
    } finally {
      setMatchesLoading(false);
    }
  };

  const loadProfileData = useCallback(async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    try {
      const userRef = doc(db, 'userProfiles', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        
        // Merge root level data with onboardingData (root level takes priority)
        const firstName = data.firstName || data.onboardingData?.firstName || '';
        const lastName = data.lastName || data.onboardingData?.lastName || '';
        const displayName = data.displayName || data.onboardingData?.displayName || `${firstName} ${lastName}`.trim();
        
        const loadedData = {
          ...(data.onboardingData || {}),
          // Override with root level fields if they exist
          firstName,
          lastName,
          displayName,
          phone: data.phone || data.phoneNumber || data.onboardingData?.phone || data.onboardingData?.phoneNumber,
          phoneNumber: data.phoneNumber || data.phone || data.onboardingData?.phoneNumber || data.onboardingData?.phone,
          company: data.company || data.onboardingData?.company,
          companyLocation: data.companyLocation || data.onboardingData?.companyLocation,
          region: data.region || data.onboardingData?.region,
          postcode: data.postcode || data.onboardingData?.postcode,
          city: data.city || data.onboardingData?.city,
          workScope: data.workScope || data.onboardingData?.workScope,
          expertise: data.expertise || data.onboardingData?.expertise,
          interests: data.interests || data.onboardingData?.interests,
          photoPosition: data.photoPosition || data.onboardingData?.photoPosition || { x: 50, y: 50 }
        };
        
        console.log('📥 Loaded profile data:', {
          phone: loadedData.phone,
          phoneNumber: loadedData.phoneNumber,
          rootPhone: data.phone,
          rootPhoneNumber: data.phoneNumber,
          onboardingPhone: data.onboardingData?.phone
        });
        
        setOnboardingData(loadedData);
        setEditedData(loadedData);
        
        // Parse social connections
        const connections: SocialConnection[] = [
          {
            platform: 'Google Calendar',
            connected: !!(data.googleCalendarConnected || data.integrations?.googleCalendar),
            username: data.email || '',
            accessToken: data.googleCalendarToken,
          },
          {
            platform: 'Email',
            connected: !!data.email,
            username: data.email || '',
          },
          {
            platform: 'LinkedIn',
            connected: !!data.linkedinAccessToken,
            username: data.linkedin || '',
            accessToken: data.linkedinAccessToken,
          },
          {
            platform: 'Twitter',
            connected: !!data.twitterAccessToken,
            accessToken: data.twitterAccessToken,
          },
          {
            platform: 'Facebook',
            connected: !!data.facebookAccessToken,
            accessToken: data.facebookAccessToken,
          },
          {
            platform: 'Instagram',
            connected: !!data.instagramAccessToken,
            accessToken: data.instagramAccessToken,
          },
        ];
        setSocialConnections(connections);
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  // Load all profile data
  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  useEffect(() => {
    if (user?.uid && userProfile && onboardingData) {
      loadTopMatches();
    }
  }, [user?.uid, userProfile, onboardingData]);

  // Laad subscription (voor Founding Member badge)
  useEffect(() => {
    if (!user?.uid) return;
    import('firebase/firestore').then(({ doc, getDoc }) =>
      import('../firebase').then(({ db: _db }) =>
        getDoc(doc(_db, 'userProfiles', user.uid)).then(snap => {
          if (snap.exists()) {
            const sub = snap.data().subscription;
            if (sub?.tier) setUserSubscription({ tier: sub.tier, lifetimeAccessNumber: sub.lifetimeAccessNumber });
          }
        }).catch(() => {})
      )
    );
  }, [user?.uid]);

  // Scroll to settings section when settings tab is activated
  useEffect(() => {
    if (activeTab === 'settings') {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const settingsElement = document.getElementById('profiel-instellingen');
        if (settingsElement) {
          settingsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [activeTab]);

  // Listen for Google Calendar connection events
  useEffect(() => {
    const handleGoogleCalendarConnected = (event: any) => {
      console.log('✅ Google Calendar connected event received:', event.detail);
      // Reload profile data to update connection status
      loadProfileData();
      setSaving(false);
      alert('✅ Google Calendar succesvol gekoppeld!');
    };

    const handleGoogleCalendarError = (event: any) => {
      console.error('❌ Google Calendar error:', event.detail);
      setSaving(false);
      alert(`❌ Fout bij koppelen van Google Calendar: ${event.detail}`);
    };

    window.addEventListener('googleCalendarConnected', handleGoogleCalendarConnected);
    window.addEventListener('googleCalendarError', handleGoogleCalendarError);

    return () => {
      window.removeEventListener('googleCalendarConnected', handleGoogleCalendarConnected);
      window.removeEventListener('googleCalendarError', handleGoogleCalendarError);
    };
  }, [loadProfileData]);

  const handleConnectSocial = async (platform: string) => {
    if (platform === 'Google Calendar') {
      try {
        setSaving(true);
        // Ensure Google Identity Services is loaded
        await loadGoogleIdentityServices();
        // Start Google Calendar OAuth flow
        await connectGoogleCalendar();
        // Note: connectGoogleCalendar will trigger events when done
        // The useEffect above will handle the success/error events
      } catch (error: any) {
        console.error('Error connecting Google Calendar:', error);
        alert(`❌ Fout bij koppelen van Google Calendar: ${error.message || 'Onbekende fout'}`);
        setSaving(false);
      }
    } else if (platform === 'LinkedIn') {
      try {
        sessionStorage.setItem('linkedinReturnUrl', window.location.hash || '#profile-hub');
        linkedinOAuth.startOAuthFlow();
      } catch (error: any) {
        console.error('Error starting LinkedIn OAuth:', error);
        alert(`❌ Fout bij koppelen van LinkedIn: ${error.message || 'Onbekende fout'}`);
      }
    } else {
      alert(`${platform} koppeling wordt binnenkort toegevoegd!`);
    }
  };

  const handleDisconnectSocial = async (platform: string) => {
    if (!user?.uid) return;
    
    // Prevent disconnecting email
    if (platform === 'Email') {
      alert('Email kan niet ontkoppeld worden - dit is je primaire account identificatie.');
      return;
    }
    
    setSaving(true);
    try {
      const userRef = doc(db, 'userProfiles', user.uid);
      const updateData: any = {
        updatedAt: serverTimestamp()
      };
      
      if (platform === 'Google Calendar') {
        updateData.googleCalendarConnected = false;
        updateData.googleCalendarToken = null;
      } else if (platform === 'LinkedIn') {
        updateData.linkedinAccessToken = null;
        updateData.linkedin = null;
      } else if (platform === 'Twitter') {
        updateData.twitterAccessToken = null;
      } else if (platform === 'Facebook') {
        updateData.facebookAccessToken = null;
      } else if (platform === 'Instagram') {
        updateData.instagramAccessToken = null;
      }
      
      await updateDoc(userRef, updateData);
      
      setSocialConnections(prev => 
        prev.map(conn => 
          conn.platform === platform 
            ? { ...conn, connected: false, accessToken: undefined, username: undefined }
            : conn
        )
      );
      
      alert(`✅ ${platform} ontkoppeld`);
    } catch (error) {
      console.error('Error disconnecting social:', error);
      alert('❌ Er is een fout opgetreden');
    } finally {
      setSaving(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleProfileSettingsUpdated = (updatedProfile: any) => {
    if (updatedProfile) {
      onProfileUpdate?.(updatedProfile);
    }
    loadProfileData();
    setActiveTab('overview');
  };

  // Handle inline field editing
  const startEditingField = (fieldName: string, currentValue: string) => {
    setEditingField(fieldName);
    setTempFieldValue(currentValue || '');
  };

  const cancelEditingField = () => {
    setEditingField(null);
    setTempFieldValue('');
  };

  const saveField = async (fieldName: string) => {
    if (!user?.uid) return;
    
    setSaving(true);
    try {
      const userRef = doc(db, 'userProfiles', user.uid);
      
      // Prepare update data - save both in onboardingData AND at root level
      const updateData: any = {
        [fieldName]: tempFieldValue, // Save at root level
        [`onboardingData.${fieldName}`]: tempFieldValue, // Also save in onboardingData
        updatedAt: serverTimestamp()
      };
      
      // If it's phoneNumber, also update phone field (both root and in onboardingData)
      if (fieldName === 'phoneNumber') {
        updateData.phone = tempFieldValue;
        updateData['onboardingData.phone'] = tempFieldValue;
        updateData.phoneNumber = tempFieldValue;
        updateData['onboardingData.phoneNumber'] = tempFieldValue;
      }
      
      console.log('💾 Saving field:', fieldName, 'Value:', tempFieldValue);
      console.log('💾 Update data:', updateData);
      
      await updateDoc(userRef, updateData);
      
      // Update local state
      setOnboardingData(prev => ({ ...prev, [fieldName]: tempFieldValue }));
      setEditedData(prev => ({ ...prev, [fieldName]: tempFieldValue }));
      setEditingField(null);
      setTempFieldValue('');
      
      console.log('✅ Field saved successfully:', fieldName);
      alert('✅ Veld succesvol bijgewerkt!');
    } catch (error) {
      console.error('❌ Error saving field:', error);
      alert('❌ Er is een fout opgetreden bij het opslaan.');
    } finally {
      setSaving(false);
    }
  };

  // Save all edited data
  const saveAllData = async () => {
    if (!user?.uid) return;
    
    setSaving(true);
    try {
      const userRef = doc(db, 'userProfiles', user.uid);
      
      // Prepare update data - save both in onboardingData AND at root level
      const updateData: any = {
        updatedAt: serverTimestamp()
      };
      
      // Update all fields in editedData
      Object.keys(editedData).forEach(key => {
        const value = editedData[key as keyof OnboardingData];
        if (value !== undefined && value !== null) {
          updateData[key] = value;
          updateData[`onboardingData.${key}`] = value;
        }
      });
      
      // Special handling for phoneNumber
      if (editedData.phoneNumber) {
        updateData.phone = editedData.phoneNumber;
        updateData['onboardingData.phone'] = editedData.phoneNumber;
      }
      
      // ✅ CRITICAL: Auto-compute displayName when firstName or lastName changes
      if (editedData.firstName || editedData.lastName) {
        const computedDisplayName = `${editedData.firstName || ''} ${editedData.lastName || ''}`.trim();
        if (computedDisplayName) {
          updateData.displayName = computedDisplayName;
          updateData['onboardingData.displayName'] = computedDisplayName;
          // Update editedData so local state has the new displayName
          editedData.displayName = computedDisplayName;
        }
      }
      
      console.log('💾 Saving all data:', updateData);
      
      await updateDoc(userRef, updateData);
      
      // Update local state
      setOnboardingData(editedData);
      setEditMode(false);
      
      console.log('✅ All data saved successfully');
      alert('✅ Profiel succesvol bijgewerkt!');
      
      // Trigger profile update callback to refresh parent components
      if (onProfileUpdate) {
        onProfileUpdate(editedData);
      }
    } catch (error) {
      console.error('❌ Error saving all data:', error);
      alert('❌ Er is een fout opgetreden bij het opslaan.');
    } finally {
      setSaving(false);
    }
  };

  // Handle photo upload
  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('❌ Alleen afbeeldingen zijn toegestaan.');
      return;
    }

    // Validate max raw file size (20MB) — compression handles the rest
    if (file.size > 20 * 1024 * 1024) {
      alert('❌ Bestand is te groot. Maximaal 20MB toegestaan.');
      return;
    }

    // Create preview URL
    const previewURL = URL.createObjectURL(file);
    setTempPhotoURL(previewURL);
    setTempPhotoFile(file);
    setTempPhotoPosition({ x: 50, y: 50 }); // Center by default
    setShowImagePositioner(true);
  };

  const handleImagePositionSave = async () => {
    if (!tempPhotoFile || !user?.uid) return;

    setUploadingPhoto(true);
    setShowImagePositioner(false);
    
    try {
      // Compress image before upload (max 800px for profile photos)
      console.log('🔄 Compressing image...');
      const compressedFile = await compressImage(tempPhotoFile, 800, 0.85);
      console.log('✅ Image compressed successfully');

      // Upload to Firebase Storage (using profilePhotos path that exists in storage rules)
      const photoRef = ref(storage, `profilePhotos/${user.uid}/${Date.now()}-${compressedFile.name}`);
      await uploadBytes(photoRef, compressedFile);
      const downloadURL = await getDownloadURL(photoRef);

      // Update Firebase Auth profile
      await updateProfile(user, { photoURL: downloadURL });

      // Update Firestore with photo AND position
      const userRef = doc(db, 'userProfiles', user.uid);
      await updateDoc(userRef, {
        photoURL: downloadURL,
        photoPosition: tempPhotoPosition,
        'onboardingData.photoURL': downloadURL,
        'onboardingData.photoPosition': tempPhotoPosition,
        updatedAt: serverTimestamp()
      });

      // Clean up
      URL.revokeObjectURL(tempPhotoURL);
      setTempPhotoFile(null);
      setTempPhotoURL('');
      
      // Reload profile data
      await loadProfileData();
      
      alert('✅ Profielfoto succesvol bijgewerkt!');
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      alert('❌ Er is een fout opgetreden bij het uploaden van de foto.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    updatePosition(e);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      updatePosition(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const updatePosition = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setTempPhotoPosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Icon icon={Loader} size={32} strokeWidth={1.5} color="#2563eb" className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-3 py-3 md:p-6">
      {accessNotice && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {accessNotice}
        </div>
      )}
      {/* Hero Header */}
      <div className="hidden md:block bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-8 mb-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-6 mb-4">
            <div 
              className="relative w-24 h-24 rounded-full bg-white/20 flex items-center justify-center overflow-hidden ring-4 ring-white/30 cursor-pointer group hover:ring-white/50 transition-all"
              onClick={handlePhotoClick}
              title="Klik om profielfoto te wijzigen"
            >
              {user?.photoURL ? (
                <img 
                  src={user.photoURL} 
                  alt={user.displayName || 'User'} 
                  className="w-full h-full object-cover"
                  style={{
                    objectPosition: onboardingData?.photoPosition 
                      ? `${onboardingData.photoPosition.x}% ${onboardingData.photoPosition.y}%` 
                      : 'center'
                  }}
                />
              ) : (
                <Icon icon={User} size={48} strokeWidth={1.5} />
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Icon icon={Camera} size={24} strokeWidth={1.5} color="#ffffff" />
              </div>
              {uploadingPhoto && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <Icon icon={Loader} size={24} strokeWidth={1.5} color="#ffffff" className="animate-spin" />
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-1">{onboardingData.displayName || user?.displayName || 'Gebruiker'}</h1>
              <p className="text-blue-100 text-lg">{user?.email}</p>
              {/* Founding Member badge */}
              {userSubscription?.tier === 'lifetime' && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/20 border border-amber-400/40 backdrop-blur-sm">
                  <span className="text-amber-300 text-sm">✦</span>
                  <span className="text-[11px] font-bold text-amber-200 tracking-wide">
                    FOUNDING MEMBER
                    {userSubscription.lifetimeAccessNumber ? ` #${userSubscription.lifetimeAccessNumber}` : ''}
                  </span>
                  <span className="text-amber-300 text-sm">✦</span>
                </div>
              )}
              {activeEntity && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-blue-100">
                  <span className="px-2 py-1 rounded-full bg-white/15">
                    {activeEntity.name}
                  </span>
                  <span className="px-2 py-1 rounded-full bg-white/15">
                    {activeEntity.type}
                  </span>
                  <span className="px-2 py-1 rounded-full bg-white/15">
                    Rol: {activeEntity.role === 'va' ? 'privé' : roleLabel}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="bg-white/15 rounded-full p-1 flex items-center space-x-1">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeTab === 'overview' ? 'bg-white text-blue-600 shadow-sm' : 'text-white/80 hover:text-white'
                  }`}
                >
                  Overzicht
                </button>
                <button
                  onClick={() => {
                    setActiveTab('settings');
                    // Scroll to settings section after a brief delay to ensure DOM is updated
                    setTimeout(() => {
                      const settingsElement = document.getElementById('profiel-instellingen');
                      if (settingsElement) {
                        settingsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 150);
                  }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center space-x-1 ${
                    activeTab === 'settings' ? 'bg-white text-blue-600 shadow-sm' : 'text-white/80 hover:text-white'
                  }`}
                >
                  <Icon icon={Settings} size={16} strokeWidth={1.5} />
                  <span>Instellingen</span>
                </button>
              </div>
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <Icon icon={XCircle} size={24} strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-2">
              <Icon icon={Briefcase} size={16} strokeWidth={1.5} />
              <span>{onboardingData.company || 'Geen bedrijf'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon icon={MapPin} size={16} strokeWidth={1.5} />
              <span>{onboardingData.companyLocation || 'Geen locatie'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8 sticky top-4 z-20">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-700">Navigeer naar:</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => scrollToSection('profiel-overzicht')}
              className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
            >
              <Icon icon={User} size={12} strokeWidth={1.5} className="inline mr-1" />
              Profiel
            </button>
            <button
              onClick={() => scrollToSection('entiteit-context')}
              className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-md transition-colors"
            >
              <Icon icon={Layout} size={12} strokeWidth={1.5} className="inline mr-1" />
              Entiteiten
            </button>
            <button
              onClick={() => scrollToSection('toegang-rollen')}
              className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-md transition-colors"
            >
              <Icon icon={Award} size={12} strokeWidth={1.5} className="inline mr-1" />
              Rollen
            </button>
            <button
              onClick={() => scrollToSection('uitnodigingen')}
              className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
            >
              <Icon icon={User} size={12} strokeWidth={1.5} className="inline mr-1" />
              Uitnodigen
            </button>
            <button
              onClick={() => scrollToSection('profielzichtbaarheid')}
              className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors"
            >
              <Icon icon={Award} size={12} strokeWidth={1.5} className="inline mr-1" />
              Zichtbaarheid
            </button>
            <button
              onClick={() => scrollToSection('onboarding-data')}
              className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
            >
              <Icon icon={Briefcase} size={12} strokeWidth={1.5} className="inline mr-1" />
              Data
            </button>
            <button
              onClick={() => scrollToSection('relevante-connecties')}
              className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
            >
              <Icon icon={User} size={12} strokeWidth={1.5} className="inline mr-1" />
              Connecties
            </button>
            <button
              onClick={() => scrollToSection('social-koppelingen')}
              className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
            >
              <Icon icon={LinkIcon} size={12} strokeWidth={1.5} className="inline mr-1" />
              Koppelingen
            </button>
            <button
              onClick={() => scrollToSection('lidmaatschap')}
              className="px-3 py-1.5 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors"
            >
              <Icon icon={CreditCard} size={12} strokeWidth={1.5} className="inline mr-1" />
              Lidmaatschap
            </button>
            <button
              onClick={() => scrollToSection('instellingen')}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Icon icon={Settings} size={12} strokeWidth={1.5} className="inline mr-1" />
              Instellingen
            </button>
          </div>
        </div>
      </div>

      {/* Journey Sections */}
      {activeTab === 'overview' ? (
        <div className="space-y-8">

        {/* ── Mobile-only: Weergave & Filters ────────────────────────────── */}
        <div id="mobile-weergave-filters" className="md:hidden space-y-3 scroll-mt-16">
          <h2 style={{ fontSize: '13px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', paddingLeft: '4px' }}>
            Weergave &amp; Filters
          </h2>

          {/* Display Instellingen */}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #F0EEE9', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <p style={{ fontSize: '14px', fontWeight: 600, color: '#3C4F49', marginBottom: '12px' }}>Display Instellingen</p>
            {[
              { key: 'focusMode',       label: 'Focus Mode',    color: '#EC4899' },
              { key: 'energyMode',      label: 'Energy Mode',   color: '#7A9488' },
              { key: 'blueLightFilter', label: 'Blauw Licht',   color: '#3B82F6' },
            ].map(({ key, label, color }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '10px', marginBottom: '10px', borderBottom: '1px solid #F4F2ED' }}>
                <span style={{ fontSize: '14px', color: '#374151' }}>{label}</span>
                <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                    checked={!!(displaySettings as any)?.[key]}
                    onChange={(e) => onDisplaySettingsChange?.({ ...displaySettings, [key]: e.target.checked })}
                  />
                  <span style={{
                    display: 'block', width: '44px', height: '24px', borderRadius: '12px',
                    background: (displaySettings as any)?.[key] ? color : '#E5E7EB',
                    position: 'relative', transition: 'background 0.2s',
                  }}>
                    <span style={{
                      position: 'absolute', top: '2px',
                      left: (displaySettings as any)?.[key] ? '22px' : '2px',
                      width: '20px', height: '20px', borderRadius: '50%',
                      background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      transition: 'left 0.2s',
                    }} />
                  </span>
                </label>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', color: '#374151' }}>Dag/Nacht</span>
              <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                  checked={displaySettings?.dayNightMode === 'night'}
                  onChange={(e) => onDisplaySettingsChange?.({ ...displaySettings, dayNightMode: e.target.checked ? 'night' : 'day' })}
                />
                <span style={{
                  display: 'block', width: '44px', height: '24px', borderRadius: '12px',
                  background: displaySettings?.dayNightMode === 'night' ? '#7C3AED' : '#E5E7EB',
                  position: 'relative', transition: 'background 0.2s',
                }}>
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: displaySettings?.dayNightMode === 'night' ? '22px' : '2px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    transition: 'left 0.2s',
                  }} />
                </span>
              </label>
            </div>
          </div>

          {/* Clean Mind Filter */}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #F0EEE9', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <p style={{ fontSize: '14px', fontWeight: 600, color: '#3C4F49', margin: 0 }}>Clean Mind Filter</p>
              <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                  checked={cleanMindEnabled}
                  onChange={(e) => updateCleanMind(e.target.checked)}
                />
                <span style={{
                  display: 'block', width: '44px', height: '24px', borderRadius: '12px',
                  background: cleanMindEnabled ? '#22C55E' : '#E5E7EB',
                  position: 'relative', transition: 'background 0.2s',
                }}>
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: cleanMindEnabled ? '22px' : '2px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    transition: 'left 0.2s',
                  }} />
                </span>
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
              {[
                { key: 'videos',  label: "Video's" },
                { key: 'images',  label: 'Afbeeldingen' },
                { key: 'photos',  label: "Foto's" },
                { key: 'text',    label: 'Tekst' },
                { key: 'ai',      label: 'A.I.-content' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => updateCleanFilters(f => ({ ...f, [key]: !(f as any)[key] }))}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    fontSize: '12px',
                    fontWeight: 500,
                    background: (cleanFilters as any)[key] ? '#fff' : '#F3F4F6',
                    color: (cleanFilters as any)[key] ? '#1F2937' : '#6B7280',
                    cursor: 'pointer',
                    textAlign: 'left' as const,
                  }}
                >
                  {label}
                  {!(cleanFilters as any)[key] && (
                    <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#22C55E', marginLeft: '6px', verticalAlign: 'middle' }} />
                  )}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: '#374151' }}>Emoji's verwijderen</span>
              <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                  checked={removeEmojis}
                  onChange={(e) => updateRemoveEmojis(e.target.checked)}
                />
                <span style={{
                  display: 'block', width: '44px', height: '24px', borderRadius: '12px',
                  background: removeEmojis ? '#22C55E' : '#E5E7EB',
                  position: 'relative', transition: 'background 0.2s',
                }}>
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: removeEmojis ? '22px' : '2px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    transition: 'left 0.2s',
                  }} />
                </span>
              </label>
            </div>
          </div>
        </div>
        {/* ── End mobile settings ─────────────────────────────────────────── */}

        {/* Section: Entiteit context */}
        <div id="entiteit-context" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-slate-400 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <Icon icon={Layout} size={24} strokeWidth={1.5} color="#475569" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Actieve entiteit</h2>
                <p className="text-sm text-gray-600">Alle acties gebeuren binnen deze entiteit.</p>
              </div>
            </div>

            <EntitySwitcher />
            
            {/* ✅ NIEUW: Entity Management Buttons */}
            <div className="mt-4 flex items-center space-x-3">
              <button
                onClick={() => setShowCreateEntity(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
              >
                <Icon icon={Building2} size={16} strokeWidth={1.5} />
                <span>Nieuwe Entiteit</span>
              </button>
              <button
                onClick={() => setShowJoinEntity(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                <Icon icon={Users} size={16} strokeWidth={1.5} />
                <span>Lid Worden</span>
              </button>
            </div>
          </div>
        </div>

        {/* Section: Toegang & Rollen */}
        <div id="toegang-rollen" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-amber-500 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Icon icon={Award} size={24} strokeWidth={1.5} color="#d97706" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Toegang & Rollen</h2>
                <p className="text-sm text-gray-600">Overzicht van leden en expliciete rechten.</p>
              </div>
            </div>
            <AccessRolesSection entityId={activeEntityId} />
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Rechtenmatrix</h3>
              <p className="text-sm text-gray-600 mb-3">Wijzig rechten per gebruiker, per pagina en onderdeel.</p>
              <PermissionsMatrix
                entityId={activeEntityId}
                currentUserId={user?.uid}
                canManage={!!canManagePermissions}
              />
            </div>
          </div>
        </div>

        {/* Section: Uitnodigingen */}
        <div id="uitnodigingen" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-indigo-500 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Icon icon={User} size={24} strokeWidth={1.5} color="#4f46e5" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Uitnodigingen</h2>
                <p className="text-sm text-gray-600">Nodig V,A’s of bestuursleden uit met expliciete rechten.</p>
              </div>
            </div>

            <div className="space-y-4">
              {!activeEntity && (
                <p className="text-sm text-gray-600">
                  Selecteer eerst een entiteit om uitnodigingen te versturen.
                </p>
              )}
              <div>
                <button
                  onClick={() => setShowVaInvite(prev => !prev)}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
                  disabled={!activeEntity}
                >
                  + Nodig V,A uit
                </button>
                {showVaInvite && (
                  <div className="mt-4">
                    <InviteRoleForm
                      role="va"
                      title="V,A uitnodigen"
                      description="Selecteer expliciete rechten per pagina en onderdeel."
                      activeEntity={activeEntity}
                      currentUserId={user?.uid}
                    />
                  </div>
                )}
              </div>

              {activeEntity?.type === 'networkclub' && activeEntity.role === 'organizer' && (
                <div>
                  <button
                    onClick={() => setShowBoardInvite(prev => !prev)}
                    className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-md text-sm font-medium"
                  >
                    Nodig bestuurslid uit
                  </button>
                  {showBoardInvite && (
                    <div className="mt-3">
                      <InviteRoleForm
                        role="board"
                        title="Bestuurslid uitnodigen"
                        description="Bestuursleden kunnen geen rollen beheren en geen eigenaar wijzigen."
                        activeEntity={activeEntity}
                        restrictToNetworkclub
                        currentUserId={user?.uid}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Manager Access Management - Only for company owners (entrepreneurs) */}
              {user?.uid && onboardingData.accountType === 'entrepreneur' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <ManagerAccessManagement companyOwnerId={user.uid} />
                  
                  {/* Account Transfer Button */}
                  <div className="mt-6 pt-6 border-t border-red-200">
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-red-900 mb-1">Account Overdragen</h4>
                          <p className="text-sm text-red-700">
                            Draag het volledige eigenaarschap van je bedrijf over aan een andere gebruiker. 
                            <strong className="block mt-1">Let op: Deze actie is onomkeerbaar!</strong>
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowAccountTransfer(true)}
                        className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        Start Overdracht
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section: Profielzichtbaarheid */}
        <div id="profielzichtbaarheid" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-emerald-500 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Icon icon={Award} size={24} strokeWidth={1.5} color="#059669" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Profielzichtbaarheid</h2>
                <p className="text-sm text-gray-600">Rollen en herkenning van entiteiten.</p>
              </div>
            </div>

            {!activeEntity ? (
              <p className="text-sm text-gray-600">Selecteer een entiteit om zichtbaarheid te bekijken.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Entiteittype:</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                    {activeEntity.type}
                  </span>
                  {activeEntity.type === 'networkclub' && (
                    <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Netwerkclub
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Rol zichtbaar:</span>
                  {activeEntity.role === 'va' ? (
                    <span className="text-sm text-gray-600">Niet zichtbaar (V,A is privé)</span>
                  ) : (
                    <span className="text-sm text-gray-800">{roleLabel}</span>
                  )}
                </div>
                {activeEntity.role === 'board' && (
                  <div className="text-sm text-gray-700">Bestuursrol is zichtbaar op het netwerkclubprofiel.</div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Section 1: Profiel Overzicht */}
        <div id="profiel-overzicht" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-blue-500 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon icon={User} size={24} strokeWidth={1.5} color="#2563eb" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Profiel Overzicht</h2>
                  <p className="text-sm text-gray-600">Je basis informatie en expertise</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {editMode ? (
                  <>
                    <button
                      onClick={saveAllData}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors disabled:opacity-50"
                    >
                      <Icon icon={Save} size={16} strokeWidth={1.5} />
                      <span>{saving ? 'Opslaan...' : 'Opslaan'}</span>
                    </button>
                    <button
                      onClick={() => {
                        setEditedData(onboardingData);
                        setEditMode(false);
                      }}
                      disabled={saving}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors disabled:opacity-50"
                    >
                      <Icon icon={X} size={16} strokeWidth={1.5} />
                      <span>Annuleren</span>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setEditMode(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
                  >
                    <Icon icon={Edit2} size={16} strokeWidth={1.5} />
                    <span>Bewerken</span>
                  </button>
                )}
              </div>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Basis Informatie</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-xs text-gray-600 mb-1 block">Voornaam</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editedData.firstName || ''}
                        onChange={(e) => setEditedData({...editedData, firstName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Voornaam"
                      />
                    ) : (
                      <p className="font-medium text-gray-900">{onboardingData.firstName || '-'}</p>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-xs text-gray-600 mb-1 block">Achternaam</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editedData.lastName || ''}
                        onChange={(e) => setEditedData({...editedData, lastName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Achternaam"
                      />
                    ) : (
                      <p className="font-medium text-gray-900">{onboardingData.lastName || '-'}</p>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-xs text-gray-600 mb-1 block">Bedrijf</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editedData.company || ''}
                        onChange={(e) => setEditedData({...editedData, company: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Bedrijfsnaam"
                      />
                    ) : (
                      <p className="font-medium text-gray-900">{onboardingData.company || '-'}</p>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors relative group">
                    <label className="text-xs text-gray-600 mb-1 block">Telefoonnummer</label>
                    {editingField === 'phoneNumber' ? (
                      <div className="flex items-center space-x-2">
                        <input
                          ref={phoneInputRef}
                          type="tel"
                          value={tempFieldValue}
                          onChange={(e) => setTempFieldValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveField('phoneNumber');
                            if (e.key === 'Escape') cancelEditingField();
                          }}
                          className="flex-1 px-3 py-2 border border-blue-500 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Bijv. +31 6 12345678"
                          autoFocus
                        />
                        <button
                          onClick={() => saveField('phoneNumber')}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                          title="Opslaan"
                        >
                          <Icon icon={Save} size={16} strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={cancelEditingField}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Annuleren"
                        >
                          <Icon icon={X} size={16} strokeWidth={1.5} />
                        </button>
                      </div>
                    ) : (
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => startEditingField('phoneNumber', onboardingData.phoneNumber || '')}
                      >
                        <p className="font-medium text-gray-900 flex-1">{onboardingData.phoneNumber || '-'}</p>
                        <Icon icon={Edit2} size={16} strokeWidth={1.5} color="#9ca3af" className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-xs text-gray-600 mb-1 block">Website</label>
                    {editMode ? (
                      <input
                        type="url"
                        value={editedData.website || ''}
                        onChange={(e) => setEditedData({...editedData, website: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://www.voorbeeld.nl"
                      />
                    ) : (
                      <p className="font-medium text-gray-900">{onboardingData.website || '-'}</p>
                    )}
                  </div>
                  <div className={`p-4 rounded-lg ${
                    !onboardingData.city && !onboardingData.companyLocation
                      ? 'bg-amber-50 border border-amber-300'
                      : 'bg-gray-50'
                  }`}>
                    <label className="text-xs text-gray-600 mb-1 block flex items-center gap-1">
                      Stad/Plaats
                      {!onboardingData.city && !onboardingData.companyLocation && (
                        <span className="text-amber-600 text-xs font-semibold">⚠ Niet ingevuld</span>
                      )}
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editedData.city || editedData.companyLocation || ''}
                        onChange={(e) => setEditedData({...editedData, city: e.target.value, companyLocation: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Bijv. Amsterdam"
                      />
                    ) : onboardingData.city || onboardingData.companyLocation ? (
                      <p className="font-medium text-gray-900">{onboardingData.city || onboardingData.companyLocation}</p>
                    ) : (
                      <div className="flex items-center justify-between">
                        <p className="text-amber-700 text-sm italic">Nog niet ingevuld</p>
                        <button
                          onClick={() => setEditMode(true)}
                          className="text-xs text-amber-700 underline hover:text-amber-900 font-medium"
                        >
                          Nu invullen →
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-xs text-gray-600 mb-1 block">Straatnaam + Huisnummer</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editedData.streetAddress || ''}
                        onChange={(e) => setEditedData({...editedData, streetAddress: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Bijv. Hoofdstraat 123"
                      />
                    ) : (
                      <p className="font-medium text-gray-900">{onboardingData.streetAddress || '-'}</p>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-xs text-gray-600 mb-1 block">Postcode</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editedData.postalCode || ''}
                        onChange={(e) => setEditedData({...editedData, postalCode: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Bijv. 1234 AB"
                      />
                    ) : (
                      <p className="font-medium text-gray-900">{onboardingData.postalCode || '-'}</p>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-xs text-gray-600 mb-1 block">Werkgebied</label>
                    {editMode ? (
                      <select
                        value={editedData.workScope || 'lokaal'}
                        onChange={(e) => setEditedData({...editedData, workScope: e.target.value as any})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="lokaal">Lokaal</option>
                        <option value="nationaal">Nationaal</option>
                        <option value="internationaal">Internationaal</option>
                      </select>
                    ) : (
                      <p className="font-medium text-gray-900 capitalize">{onboardingData.workScope || '-'}</p>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-xs text-gray-600 mb-1 block">Primaire Regio</label>
                    {editMode ? (
                      <select
                        value={editedData.region || ''}
                        onChange={(e) => setEditedData({...editedData, region: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Selecteer een regio</option>
                        <optgroup label="🇳🇱 Nederland">
                          <option value="Noord-Holland">Noord-Holland</option>
                          <option value="Zuid-Holland">Zuid-Holland</option>
                          <option value="Utrecht">Utrecht</option>
                          <option value="Flevoland">Flevoland</option>
                          <option value="Gelderland">Gelderland</option>
                          <option value="Overijssel">Overijssel</option>
                          <option value="Drenthe">Drenthe</option>
                          <option value="Friesland">Friesland</option>
                          <option value="Groningen">Groningen</option>
                          <option value="Noord-Brabant">Noord-Brabant</option>
                          <option value="Limburg">Limburg</option>
                          <option value="Zeeland">Zeeland</option>
                        </optgroup>
                        <optgroup label="🌍 Internationaal">
                          <option value="België - Vlaanderen">België - Vlaanderen</option>
                          <option value="België - Wallonië">België - Wallonië</option>
                          <option value="Duitsland">Duitsland</option>
                          <option value="Frankrijk">Frankrijk</option>
                          <option value="Verenigd Koninkrijk">Verenigd Koninkrijk</option>
                          <option value="Internationaal">Internationaal</option>
                        </optgroup>
                      </select>
                    ) : (
                      <p className="font-medium text-gray-900">{onboardingData.region || '-'}</p>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-xs text-gray-600 mb-1 block">Land</label>
                    {editMode ? (
                      <select
                        value={editedData.country || ''}
                        onChange={(e) => setEditedData({...editedData, country: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Selecteer land...</option>
                        {Object.entries(COUNTRY_FLAGS).map(([name, flag]) => (
                          <option key={name} value={name}>{flag} {name}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        {onboardingData.country ? (
                          <>
                            <span className="text-xl">{COUNTRY_FLAGS[onboardingData.country] || '🌍'}</span>
                            {onboardingData.country}
                          </>
                        ) : '-'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Expertise</h3>
                {editMode ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(editedData.expertise || []).map((skill, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center space-x-2">
                          <span>{skill}</span>
                          <button
                            onClick={() => {
                              const newExpertise = (editedData.expertise || []).filter((_, i) => i !== idx);
                              setEditedData({...editedData, expertise: newExpertise});
                            }}
                            className="text-blue-700 hover:text-blue-900"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        placeholder="Voeg expertise toe..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                            const newExpertise = [...(editedData.expertise || []), e.currentTarget.value.trim()];
                            setEditedData({...editedData, expertise: newExpertise});
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {onboardingData.expertise && onboardingData.expertise.length > 0 ? (
                      onboardingData.expertise.map((skill, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          {skill}
                        </span>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">Geen expertise toegevoegd</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Interesses</h3>
                {editMode ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(editedData.interests || []).map((interest, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center space-x-2">
                          <span>{interest}</span>
                          <button
                            onClick={() => {
                              const newInterests = (editedData.interests || []).filter((_, i) => i !== idx);
                              setEditedData({...editedData, interests: newInterests});
                            }}
                            className="text-green-700 hover:text-green-900"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        placeholder="Voeg interesse toe..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                            const newInterests = [...(editedData.interests || []), e.currentTarget.value.trim()];
                            setEditedData({...editedData, interests: newInterests});
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {onboardingData.interests && onboardingData.interests.length > 0 ? (
                      onboardingData.interests.map((interest, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          {interest}
                        </span>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">Geen interesses toegevoegd</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Onboarding Data */}
        <div id="onboarding-data" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-green-500 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon icon={Briefcase} size={24} strokeWidth={1.5} color="#16a34a" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Onboarding Data</h2>
                  <p className="text-sm text-gray-600">Informatie voor matching en personalisatie</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="border-l-4 border-blue-400 pl-4 py-2">
                <h3 className="font-semibold text-gray-900 mb-3">Persoonlijke Informatie</h3>
                <div className="space-y-2 text-sm">
                  {editMode ? (
                    <div className="space-y-2">
                      <div>
                        <label className="text-gray-600 block mb-1">Naam:</label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={editedData.firstName || ''}
                            onChange={(e) => setEditedData({...editedData, firstName: e.target.value})}
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Voornaam"
                          />
                          <input
                            type="text"
                            value={editedData.lastName || ''}
                            onChange={(e) => setEditedData({...editedData, lastName: e.target.value})}
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Achternaam"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-gray-600 block mb-1">Bedrijf:</label>
                        <input
                          type="text"
                          value={editedData.company || ''}
                          onChange={(e) => setEditedData({...editedData, company: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Bedrijfsnaam"
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 block mb-1">Locatie:</label>
                        <input
                          type="text"
                          value={editedData.companyLocation || ''}
                          onChange={(e) => setEditedData({...editedData, companyLocation: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Locatie"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <p><span className="text-gray-600">Naam:</span> <span className="font-medium">{onboardingData.firstName} {onboardingData.lastName}</span></p>
                      <p><span className="text-gray-600">Bedrijf:</span> <span className="font-medium">{onboardingData.company}</span></p>
                      <p><span className="text-gray-600">Locatie:</span> <span className="font-medium">{onboardingData.companyLocation}</span></p>
                    </>
                  )}
                </div>
              </div>

              <div className="border-l-4 border-green-400 pl-4 py-2">
                <h3 className="font-semibold text-gray-900 mb-3">Werkgebied & Regio's</h3>
                <div className="space-y-2 text-sm">
                  {editMode ? (
                    <div className="space-y-2">
                      <div>
                        <label className="text-gray-600 block mb-1">Werkgebied:</label>
                        <select
                          value={editedData.workScope || 'lokaal'}
                          onChange={(e) => setEditedData({...editedData, workScope: e.target.value as any})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="lokaal">Lokaal</option>
                          <option value="nationaal">Nationaal</option>
                          <option value="internationaal">Internationaal</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-gray-600 block mb-1">Land:</label>
                        <select
                          value={editedData.country || ''}
                          onChange={(e) => setEditedData({...editedData, country: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="">Selecteer land...</option>
                          {Object.entries(COUNTRY_FLAGS).map(([name, flag]) => (
                            <option key={name} value={name}>{flag} {name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-gray-600 block mb-1">Primaire Regio:</label>
                        <input
                          type="text"
                          value={editedData.region || ''}
                          onChange={(e) => setEditedData({...editedData, region: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="Primaire Regio"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <p><span className="text-gray-600">Werkgebied:</span> <span className="font-medium capitalize">{onboardingData.workScope}</span></p>
                      <p><span className="text-gray-600">Land:</span> <span className="font-medium">{onboardingData.country ? `${COUNTRY_FLAGS[onboardingData.country] || '🌍'} ${onboardingData.country}` : '-'}</span></p>
                      <p><span className="text-gray-600">Primaire Regio:</span> <span className="font-medium">{onboardingData.region}</span></p>
                      {onboardingData.eventPrefs?.regions && onboardingData.eventPrefs.regions.length > 0 && (
                        <div>
                          <span className="text-gray-600">Geselecteerde Regio's:</span>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {onboardingData.eventPrefs.regions.map((region, idx) => (
                              <span key={idx} className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs border border-green-200">
                                {region}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="border-l-4 border-purple-400 pl-4 py-2">
                <h3 className="font-semibold text-gray-900 mb-3">Event Voorkeuren</h3>
                <div className="space-y-2 text-sm">
                  {editMode ? (
                    <div>
                      <label className="text-gray-600 block mb-1">Voorkeur:</label>
                      <select
                        value={editedData.eventPrefs?.mode || ''}
                        onChange={(e) => setEditedData({
                          ...editedData,
                          eventPrefs: {
                            ...editedData.eventPrefs,
                            mode: e.target.value as any
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecteer voorkeur</option>
                        <option value="online">Online</option>
                        <option value="hybride">Hybride</option>
                        <option value="fysiek">Fysiek</option>
                      </select>
                    </div>
                  ) : (
                    <p><span className="text-gray-600">Voorkeur:</span> <span className="font-medium capitalize">{onboardingData.eventPrefs?.mode || 'Niet ingesteld'}</span></p>
                  )}
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> Houd je profiel up-to-date voor betere matches en connecties!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Relevante Connecties */}
        <div id="relevante-connecties" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-indigo-500 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🤝</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Relevante Connecties</h2>
                  <p className="text-sm text-gray-600">Gebaseerd op je profiel en interesses</p>
                </div>
              </div>
            </div>

            {matchesLoading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-20 bg-gray-200 rounded-lg"></div>
                <div className="h-20 bg-gray-200 rounded-lg"></div>
                <div className="h-20 bg-gray-200 rounded-lg"></div>
              </div>
            ) : topMatches.length > 0 ? (
              <div className="space-y-4">
                {topMatches.map((match) => (
                  <div key={match.userId} className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1 min-w-0">
                        {match.userPhoto ? (
                          <img 
                            src={match.userPhoto} 
                            alt={match.userName} 
                            className="w-12 h-12 rounded-full object-cover flex-shrink-0" 
                          />
                        ) : (
                          <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-semibold text-lg">
                              {match.userName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 text-base">{match.userName}</h3>
                            {match.matchData.matchScore > 0 && (
                              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                                {match.matchData.matchScore}% match
                              </span>
                            )}
                          </div>
                          {match.userCompany && (
                            <p className="text-sm text-gray-600 mb-2">{match.userCompany}</p>
                          )}
                          {match.matchData.matchReasons.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {match.matchData.matchReasons.slice(0, 3).map((reason, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded border border-indigo-200">
                                  {reason}
                                </span>
                              ))}
                            </div>
                          )}
                          {match.userBio && (
                            <p className="text-sm text-gray-600 line-clamp-2">{match.userBio}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      window.location.hash = '#connecties';
                    }}
                    className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors text-sm"
                  >
                    Bekijk alle connecties →
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">Nog geen relevante connecties gevonden</p>
                <button
                  onClick={() => {
                    window.location.hash = '#connecties';
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors text-sm"
                >
                  Bekijk alle beschikbare connecties →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Section 4: Social Media Koppelingen */}
        <div id="social-koppelingen" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-purple-500 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Icon icon={LinkIcon} size={24} strokeWidth={1.5} color="#9333ea" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Social Media Koppelingen</h2>
                <p className="text-sm text-gray-600">Koppel je accounts voor automatische content deling</p>
              </div>
            </div>

            <div className="space-y-4">
              {socialConnections.map((connection) => {
                // Special styling for Google Calendar and Email
                const isEssential = connection.platform === 'Google Calendar' || connection.platform === 'Email';
                const isPrimary = connection.platform === 'Email';
                
                return (
                  <div
                    key={connection.platform}
                    className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                      isEssential 
                        ? 'border-blue-300 bg-blue-50/50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        connection.connected 
                          ? isEssential ? 'bg-blue-100' : 'bg-green-100'
                          : 'bg-gray-100'
                      }`}>
                        {connection.connected ? (
                          <Icon icon={CheckCircle} size={24} strokeWidth={1.5} color={isEssential ? '#2563eb' : '#16a34a'} />
                        ) : (
                          <Icon icon={LinkIcon} size={24} strokeWidth={1.5} color="#9ca3af" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">{connection.platform}</h3>
                          {isPrimary && (
                            <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full font-medium">
                              Primair
                            </span>
                          )}
                          {isEssential && !isPrimary && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                              Essentieel
                            </span>
                          )}
                        </div>
                        {connection.connected ? (
                          <p className={`text-sm ${isEssential ? 'text-blue-600' : 'text-green-600'}`}>
                            ✓ Gekoppeld {connection.username && `• ${connection.username}`}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500">Niet gekoppeld</p>
                        )}
                      </div>
                    </div>
                    <div>
                      {connection.connected ? (
                        <button
                          onClick={() => handleDisconnectSocial(connection.platform)}
                          disabled={saving || isPrimary}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            isPrimary
                              ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                              : 'text-red-600 hover:bg-red-50 disabled:opacity-50'
                          }`}
                          title={isPrimary ? 'Primaire email kan niet ontkoppeld worden' : ''}
                        >
                          {isPrimary ? 'Vergrendeld' : 'Ontkoppel'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConnectSocial(connection.platform)}
                          disabled={saving}
                          className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                          Koppel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                🔒 <strong>Privacy:</strong> Je social media koppelingen worden veilig opgeslagen en alleen gebruikt voor functies die je zelf activeert.
              </p>
            </div>
          </div>
        </div>

        {/* Section 5: Lidmaatschap */}
        <div id="lidmaatschap" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-orange-500 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Icon icon={CreditCard} size={24} strokeWidth={1.5} color="#ea580c" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Lidmaatschap</h2>
                <p className="text-sm text-gray-600">Beheer je abonnement en voordelen</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Free Tier</h3>
                  <p className="text-sm text-gray-600">Basis toegang tot de community</p>
                </div>
                <Icon icon={Award} size={48} strokeWidth={1.5} color="#f97316" />
              </div>
              <button className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-lg font-medium transition-colors">
                Upgrade naar Premium
              </button>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                ℹ️ <strong>Info:</strong> Voor volledige lidmaatschap opties, ga naar het Lidmaatschap menu in je profiel dropdown.
              </p>
            </div>
          </div>
        </div>

        {/* Section 5: Training Center - Uitgeschakeld voor nu */}
        {/* <div id="training-center" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-indigo-500 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Icon icon={BookOpen} size={24} strokeWidth={1.5} color="#4f46e5" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Training Center</h2>
                <p className="text-sm text-gray-600">Je leertraject en voortgang</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
              <p className="text-gray-700 mb-4">Toegang tot exclusieve trainingen en workshops</p>
              <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-medium transition-colors">
                Ga naar Training Center
              </button>
            </div>
          </div>
        </div> */}

        {/* Section 6: Kaarten Beheer - Uitgeschakeld voor nu */}
        {/* <div id="kaarten-beheer" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-pink-500 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                <Icon icon={Layout} size={24} strokeWidth={1.5} color="#db2777" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Kaarten Beheer</h2>
                <p className="text-sm text-gray-600">Beheer je showcase en content kaarten</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg border border-pink-200">
              <p className="text-gray-700 mb-4">Personaliseer je profiel met custom kaarten</p>
              <button
                onClick={() => {
                  try {
                    // Use navigation helper
                    navigateTo('cards-editor');
                  } catch (error) {
                    console.warn('Could not navigate to cards editor:', error);
                  }
                }}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white py-3 rounded-lg font-medium transition-colors"
              >
                Beheer Kaarten
              </button>
            </div>
          </div>
        </div> */}

        {/* Section 8: Instellingen */}
        <div id="instellingen" className="scroll-mt-24">
          <div className="bg-white rounded-lg shadow-sm border-l-4 border-gray-500 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon icon={Settings} size={24} strokeWidth={1.5} color="#4b5563" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Account Instellingen</h2>
                <p className="text-sm text-gray-600">Beheer je voorkeuren en privacy</p>
              </div>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Email Notificaties</h3>
                <p className="text-sm text-gray-600 mb-3">Ontvang updates over nieuwe connecties en berichten</p>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="form-checkbox h-4 w-4 text-blue-600 rounded" defaultChecked />
                  <span className="text-sm text-gray-700">Email notificaties inschakelen</span>
                </label>
              </div>

              <PrivacySettings userId={user?.uid || ''} />

              {/* Privacybeleid — inklapbaar */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setPrivacyPolicyOpen(prev => !prev)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(139,165,155,0.15)' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#8BA59B" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Privacybeleid</p>
                      <p className="text-xs text-gray-500">AVG / GDPR conform — versie 1.0</p>
                    </div>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${privacyPolicyOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {privacyPolicyOpen && (
                  <div className="overflow-y-auto max-h-[600px] border-t border-gray-100">
                    <PrivacyPolicy embedded />
                  </div>
                )}
              </div>

              {/* Algemene Voorwaarden — inklapbaar */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setTermsOfServiceOpen(prev => !prev)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(139,165,155,0.15)' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#8BA59B" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Algemene Voorwaarden</p>
                      <p className="text-xs text-gray-500">Clean Mind platformafspraken — versie 1.0</p>
                    </div>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${termsOfServiceOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {termsOfServiceOpen && (
                  <div className="overflow-y-auto max-h-[600px] border-t border-gray-100">
                    <TermsOfService />
                  </div>
                )}
              </div>

              <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                <h3 className="font-medium text-red-900 mb-2">Danger Zone</h3>
                <p className="text-sm text-red-700 mb-3">Permanente acties die niet ongedaan gemaakt kunnen worden</p>
                <button className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 hover:bg-red-50 rounded-lg transition-colors">
                  Account Verwijderen
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
      ) : (
        <div id="profiel-instellingen" className="scroll-mt-24">
          {user && userProfile ? (
            <ProfileSettings
              user={user}
              userProfile={userProfile}
              embedded
              onClose={() => setActiveTab('overview')}
              onProfileUpdate={handleProfileSettingsUpdated}
            />
          ) : (
            <div className="bg-white rounded-lg border shadow-sm p-6 text-center">
              <p className="text-gray-600">Profiel wordt geladen...</p>
            </div>
          )}
        </div>
      )}

      {/* ✅ NIEUW: Entity Management Modals */}
      {showCreateEntity && (
        <CreateEntityModal
          onClose={() => setShowCreateEntity(false)}
          onSuccess={() => {
            setShowCreateEntity(false);
            // Reload entities
            window.location.reload();
          }}
        />
      )}

      {showJoinEntity && (
        <JoinEntityModal
          onClose={() => setShowJoinEntity(false)}
          onSuccess={() => {
            setShowJoinEntity(false);
            // Reload entities
            window.location.reload();
          }}
        />
      )}

      {/* Account Transfer Modal */}
      {showAccountTransfer && user && (
        <AccountTransferModal
          currentUserId={user.uid}
          currentUserName={onboardingData.displayName || user.displayName || 'Eigenaar'}
          currentUserEmail={user.email || ''}
          companyName={onboardingData.company || 'Mijn Bedrijf'}
          companyKvK={onboardingData.networkclubKvk || ''}
          onClose={() => setShowAccountTransfer(false)}
          onSuccess={() => {
            setShowAccountTransfer(false);
            window.location.reload();
          }}
        />
      )}

      {/* Image Positioner Modal */}
      {showImagePositioner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📐 Positioneer je foto</h3>
            <p className="text-sm text-gray-600 mb-4">
              Sleep de foto om de beste positie te vinden voor de circulaire crop
            </p>
            
            {/* Image Positioner */}
            <div className="mb-6">
              <div 
                className="relative w-full aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-move select-none"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <img
                  src={tempPhotoURL}
                  alt="Position"
                  className="w-full h-full object-cover"
                  style={{
                    objectPosition: `${tempPhotoPosition.x}% ${tempPhotoPosition.y}%`
                  }}
                  draggable={false}
                />
                {/* Circle Overlay to show crop area */}
                <div className="absolute inset-0 pointer-events-none">
                  <svg width="100%" height="100%" className="absolute inset-0">
                    <defs>
                      <mask id="circleMask">
                        <rect width="100%" height="100%" fill="white" opacity="0.7"/>
                        <circle cx="50%" cy="50%" r="40%" fill="black"/>
                      </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="black" opacity="0.5" mask="url(#circleMask)"/>
                    <circle cx="50%" cy="50%" r="40%" fill="none" stroke="white" strokeWidth="2" strokeDasharray="5,5"/>
                  </svg>
                </div>
                {/* Center crosshair */}
                <div 
                  className="absolute w-3 h-3 border-2 border-white rounded-full shadow-lg pointer-events-none"
                  style={{
                    left: `${tempPhotoPosition.x}%`,
                    top: `${tempPhotoPosition.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              </div>
              
              {/* Preview */}
              <div className="mt-4 flex items-center justify-center space-x-4">
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-2">Preview</p>
                  <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden">
                    <img
                      src={tempPhotoURL}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      style={{
                        objectPosition: `${tempPhotoPosition.x}% ${tempPhotoPosition.y}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowImagePositioner(false);
                  URL.revokeObjectURL(tempPhotoURL);
                  setTempPhotoFile(null);
                  setTempPhotoURL('');
                }}
                disabled={uploadingPhoto}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={() => setTempPhotoPosition({ x: 50, y: 50 })}
                disabled={uploadingPhoto}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ↺ Reset
              </button>
              <button
                type="button"
                onClick={handleImagePositionSave}
                disabled={uploadingPhoto}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingPhoto ? 'Opslaan...' : '✓ Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileHub;
